import { type RefObject, useCallback, useEffect, useRef, useState } from 'react';

type ElementSize = {
  width: number;
  height: number;
};

type UseResponsiveElementSize = {
  handleResize: (delay?: number) => void;
  size: ElementSize;
};

export function useResponsiveElementSize<T extends HTMLElement | null>(
  elementRef: RefObject<T>,
  debounceDelay = 100,
): UseResponsiveElementSize {
  const [size, setSize] = useState<ElementSize>({ width: 0, height: 0 });
  const resizeTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);

  const updateSize = useCallback(() => {
    const el = elementRef.current;
    if (el) {
      setSize({
        width: el.offsetWidth,
        height: el.offsetHeight,
      });
    }
  }, [elementRef.current]);

  const handleResize = useCallback(
    (delay?: number) => {
      if (resizeTimeout.current) clearTimeout(resizeTimeout.current);
      resizeTimeout.current = setTimeout(updateSize, delay ?? debounceDelay);
    },
    [updateSize, debounceDelay],
  );

  useEffect(() => {
    const el = elementRef.current;
    if (!el) return;

    const resizeObserver = new ResizeObserver(() => handleResize());
    resizeObserver.observe(el);

    return () => {
      resizeObserver.disconnect();
    };
  }, [elementRef.current, handleResize]);

  return {
    handleResize,
    size,
  };
}
